import React, { ChangeEvent } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Button } from '@material-ui/core'
import PhoneInput from 'react-phone-input-2'
import ErrorMessage from '../../components/ErrorMessage'
import CircularSpinner from '../../components/CircularSpinner'
import EditOrganizationLayout from '../../layouts/EditOrganizationLayout'
import Navbar from '../../components/Navbar'
import { useOrganizationWLSettings } from '../../hooks/useOrganizationWLSettings'
import { OrganizationSettings } from '../../dtos/OrganizationDTO'
import 'react-phone-input-2/lib/style.css'
import HeaderWithIcon from '../../components/HeaderWithIcon'
import ContactApplicationIcon from 'images/white-label/contact.png'

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  contentActions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  inputsContainer: {
    maxWidth: '540px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  socialLinksContainer: {
    maxWidth: '588px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: '24px',
    backgroundColor: theme.palette.primary[100],
  },
  description: {
    fontSize: 14,
    padding: '24px',
    backgroundColor: theme.palette.neutrals[100],
  },
  gap: {
    gap: theme.spacing(5),
  },
  w100: {
    width: '100% !important',
  },
  indicator: {
    width: '12px',
    height: '12px',
    borderRadius: '100%',
    marginLeft: '12px',
  },
}))

const statuses: Record<'pending' | 'approved'> = {
  pending: '#FFBB33',
  approved: '#00C853',
}

const EditOrganizationContact: React.FC = () => {
  const classes = useStyles()
  const {
    formData,
    isWhiteLabel,
    subDomain,
    isFormEdited,
    loadDataError,
    handleSubmit,
    loading,
    setFormData,
    setIsFormEdited,
    organizationId,
    loadOrganization,
    validationErrors,
    sendgridSenderStatus,
  } = useOrganizationWLSettings()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      if (!copy.ContactApplication) {
        copy.ContactApplication = {} as any
      }
      const { name, value } = e.target
      if (name.includes('.')) {
        const [parent, child] = name.split('.')
        if (!copy.ContactApplication[parent as keyof OrganizationSettings]) {
          copy.ContactApplication[parent as keyof OrganizationSettings] = {} as any
        }
        //  @ts-ignore
        copy.ContactApplication[parent as keyof OrganizationSettings][child] = value
      } else {
        // @ts-ignore
        copy.ContactApplication[name] = value
      }
      return copy
    })
    setIsFormEdited(true)
  }

  return (
    <div>
      <div className='hero-head'>
        <Navbar />
      </div>
      {loadDataError && <ErrorMessage text={loadDataError} />}
      {loading ? (
        <CircularSpinner className='is-center' />
      ) : (
        <EditOrganizationLayout
          organizationId={organizationId}
          reloadOrganization={loadOrganization}
          isWhiteLabel={isWhiteLabel}
          subDomain={subDomain}
          isFormEdited={isFormEdited}
          isWhiteLabelPage
        >
          <div className={classes.content}>
            <div className={classes.contentActions}>
              <HeaderWithIcon
                iconUrl={ContactApplicationIcon}
                title='Contact & Application'
                description='Configure contact forms and application settings'
              />
              <Button color='primary' variant='contained' onClick={handleSubmit}>
                Save
              </Button>
            </div>
            <div className={`columns is-flex-direction-column is-0 ${classes.gap}`}>
              <div className={`column p-0 ${classes.inputsContainer}`}>
                <div className='is-flex is-align-content-center'>
                  <div className='field'>
                    <label>Point of Contact</label>
                    <div className='control'>
                      <input
                        autoComplete='none'
                        className='input'
                        name='PointOfContactEmail'
                        value={formData.ContactApplication?.PointOfContactEmail || ''}
                        placeholder='contact@yourrganization.com'
                        onChange={handleChange}
                      />
                    </div>
                    <p className='help'>This name will appear throughout your platform</p>
                    {validationErrors?.ContactApplication?.PointOfContactEmail && (
                      <p className='help is-danger'>{validationErrors?.ContactApplication?.PointOfContactEmail}</p>
                    )}
                  </div>
                  {sendgridSenderStatus !== null && (
                    <div className={classes.indicator} style={{ backgroundColor: statuses[sendgridSenderStatus] }} />
                  )}
                </div>
                <div className='field'>
                  <label>Domain</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      name='Domain'
                      value={formData.ContactApplication?.Domain || ''}
                      placeholder='promo.yourorganization.com'
                      onChange={handleChange}
                    />
                  </div>
                  <p className='help'>Custom domain for your organization</p>
                  {validationErrors?.ContactApplication?.Domain && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.Domain}</p>
                  )}
                </div>
                <div>
                  <h4 className='is-size-5 has-text-weight-semibold mb-2'>Contact Information Displayed on Site</h4>
                  <p className='is-size-7'>This information will be shown to users on your contact page</p>
                </div>
                <div className='field'>
                  <label>Phone number</label>
                  <div className='control'>
                    <PhoneInput
                      value={formData.ContactApplication?.DisplayContactInfo?.PhoneNumber || ''}
                      country='us'
                      inputClass={classes.w100}
                      placeholder='+1 (555) 123-4567'
                      onChange={(value) =>
                        handleChange({
                          target: { name: 'DisplayContactInfo.PhoneNumber', value },
                        } as ChangeEvent<HTMLInputElement>)
                      }
                    />
                  </div>
                </div>
                <div className='field'>
                  <label>Email</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      value={formData.ContactApplication?.DisplayContactInfo?.Email || ''}
                      name='DisplayContactInfo.Email'
                      onChange={handleChange}
                      placeholder='hello@yourorganization.com'
                    />
                  </div>
                  {validationErrors?.ContactApplication?.DisplayContactInfo?.Email && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.DisplayContactInfo?.Email}</p>
                  )}
                </div>
              </div>
              <div className={classes.socialLinksContainer}>
                <h4 className='is-size-5 has-text-weight-semibold'>Social Links</h4>
                <div className='field'>
                  <label>Facebook</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      placeholder='https://facebook.com/yourorganization'
                      value={formData.ContactApplication?.Socials?.Facebook || ''}
                      name='Socials.Facebook'
                      onChange={handleChange}
                    />
                  </div>
                  {validationErrors?.ContactApplication?.Socials?.Facebook && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.Socials?.Facebook}</p>
                  )}
                </div>
                <div className='field'>
                  <label>X</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      placeholder='https://x.com/yourorganization'
                      value={formData.ContactApplication?.Socials?.X || ''}
                      name='Socials.X'
                      onChange={handleChange}
                    />
                  </div>
                  {validationErrors?.ContactApplication?.Socials?.X && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.Socials?.X}</p>
                  )}
                </div>
                <div className='field'>
                  <label>LinkedIn</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      placeholder='https://linkedin.com/company/yourorganization'
                      value={formData.ContactApplication?.Socials?.LinkedIn || ''}
                      name='Socials.LinkedIn'
                      onChange={handleChange}
                    />
                  </div>
                  {validationErrors?.ContactApplication?.Socials?.LinkedIn && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.Socials?.LinkedIn}</p>
                  )}
                </div>
                <div className='field'>
                  <label>Instagram</label>
                  <div className='control'>
                    <input
                      autoComplete='none'
                      className='input'
                      placeholder='https://instagram.com/yourorganization'
                      value={formData.ContactApplication?.Socials?.Instagram || ''}
                      name='Socials.Instagram'
                      onChange={handleChange}
                    />
                  </div>
                  {validationErrors?.ContactApplication?.Socials?.Instagram && (
                    <p className='help is-danger'>{validationErrors?.ContactApplication?.Socials?.Instagram}</p>
                  )}
                </div>
              </div>
              <p className={classes.description}>Contact settings help memebers reach your organization</p>
              <div />
            </div>
          </div>
        </EditOrganizationLayout>
      )}
    </div>
  )
}

export default EditOrganizationContact
